import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiTrendingUp } = FiIcons;

const ModuleFour = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    bestWritingTimes: '',
    bestWritingLocations: '',
    idealSessionLength: '',
    startTriggers: '',
    keepGoingTriggers: '',
    missedSessionPlan: [],
    restartPlan: '',
    reframeStatement: 'Faithful progress matters more than perfect consistency.'
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...data }));
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (option) => {
    setFormData(prev => ({
      ...prev,
      missedSessionPlan: prev.missedSessionPlan.includes(option)
        ? prev.missedSessionPlan.filter(item => item !== option)
        : [...prev.missedSessionPlan, option]
    }));
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    setTimeout(() => {
      onSave(formData, markComplete);
      setIsLoading(false);
    }, 500);
  };

  const canComplete = [
    'bestWritingTimes', 'bestWritingLocations', 'idealSessionLength',
    'startTriggers', 'keepGoingTriggers', 'restartPlan'
  ].every(field => formData[field] && formData[field].toString().trim() !== '');

  const gracePlanOptions = [
    'Remember that one missed session doesn\'t ruin everything',
    'Start with just 10 minutes the next day',
    'Review my "why" for writing this book',
    'Pray for renewed motivation and strength',
    'Adjust my schedule instead of abandoning the project',
    'Connect with my writing community for encouragement',
    'Focus on progress, not perfection'
  ];

  return (
    <ModuleLayout
      title="Sustain Momentum"
      description="Design a writing rhythm that supports faithfulness over perfection."
      icon={FiTrendingUp}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Natural Rhythm */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Natural Rhythm</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Best Writing Times
              </label>
              <input
                type="text"
                value={formData.bestWritingTimes}
                onChange={(e) => handleInputChange('bestWritingTimes', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Early morning, late evening, lunch break..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Best Writing Locations
              </label>
              <input
                type="text"
                value={formData.bestWritingLocations}
                onChange={(e) => handleInputChange('bestWritingLocations', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Home office, coffee shop, library..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ideal Session Length
              </label>
              <input
                type="text"
                value={formData.idealSessionLength}
                onChange={(e) => handleInputChange('idealSessionLength', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 30 minutes, 1 hour, 2 hours..."
              />
            </div>
          </div>
        </div>

        {/* Momentum Triggers */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Momentum Triggers</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What helps you start?
              </label>
              <textarea
                value={formData.startTriggers}
                onChange={(e) => handleInputChange('startTriggers', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="e.g., A cup of coffee, worship music, reviewing my outline, prayer..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What keeps you going?
              </label>
              <textarea
                value={formData.keepGoingTriggers}
                onChange={(e) => handleInputChange('keepGoingTriggers', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="e.g., Setting small goals, taking breaks, remembering my readers..."
              />
            </div>
          </div>
        </div>

        {/* Grace Plan */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grace Plan</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What happens when you miss a session? (Select all that apply)
              </label>
              <div className="space-y-2">
                {gracePlanOptions.map((option, index) => (
                  <label key={index} className="flex items-start">
                    <input
                      type="checkbox"
                      checked={formData.missedSessionPlan.includes(option)}
                      onChange={() => handleCheckboxChange(option)}
                      className="mt-1 mr-3 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Guilt-Free Restart Plan
              </label>
              <textarea
                value={formData.restartPlan}
                onChange={(e) => handleInputChange('restartPlan', e.target.value)}
                rows={4}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="How will you gracefully return to writing after a break or setback?"
              />
            </div>
          </div>
        </div>

        {/* Reframe Statement */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Reframe Statement</h3>
          <p className="text-sm text-gray-600 mb-3">
            Customize this statement to remind yourself of what truly matters in your writing journey.
          </p>
          <textarea
            value={formData.reframeStatement}
            onChange={(e) => handleInputChange('reframeStatement', e.target.value)}
            rows={3}
            className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-medium"
          />
        </div>

        {/* Summary */}
        {canComplete && (
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Momentum Plan</h3>
            <div className="bg-white rounded-lg p-4 space-y-3">
              <div>
                <strong className="text-sm text-gray-700">Best Writing Environment:</strong>
                <p className="text-sm text-gray-600">{formData.bestWritingTimes} at {formData.bestWritingLocations} for {formData.idealSessionLength}</p>
              </div>
              <div>
                <strong className="text-sm text-gray-700">Your Reframe:</strong>
                <p className="text-sm text-gray-600 italic">"{formData.reframeStatement}"</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleFour;